import noop from 'lodash/noop'
import React, {
  FC, useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import styled from 'styled-components'

import { ShipmentNotificationBar } from '@Components/ShipmentNotificationBar'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

const StyledBar = styled(ShipmentNotificationBar)`
  padding: 11px;

  .text {
    font-weight: bold;
    font-size: 12px;
    line-height: 14px;
    text-transform: uppercase;
  }
`

const valentinesSchedule2022: Record<string, { express?: Date, normal?: Date }> = {
  1: {
    normal: new Date('2022-02-10T15:00Z'),
  },
  3: {
    normal: new Date('2022-02-02T15:00Z'),
    express: new Date('2022-02-08T15:00Z'),
  },
  4: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  5: {
    normal: new Date('2022-02-08T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  6: {
    normal: new Date('2022-02-03T15:00Z'),
    express: new Date('2022-02-08T15:00Z'),
  },
  7: {
    normal: new Date('2022-02-08T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  8: {
    normal: new Date('2022-02-02T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  9: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  10: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  11: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  12: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  14: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  15: {
    normal: new Date('2022-02-03T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  16: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  17: {
    normal: new Date('2022-02-04T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  18: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-08T15:00Z'),
  },
  19: {
    normal: new Date('2022-02-04T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  20: {
    normal: new Date('2022-02-04T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  21: {
    normal: new Date('2022-02-02T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  22: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  23: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-08T15:00Z'),
  },
  24: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-08T15:00Z'),
  },
  25: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-08T15:00Z'),
  },
  26: {
    normal: new Date('2022-02-07T15:00Z'),
    express: new Date('2022-02-09T15:00Z'),
  },
  27: {
    normal: new Date('2022-01-31T15:00Z'),
    express: new Date('2022-02-07T15:00Z'),
  },
  28: {
    normal: new Date('2022-01-25T15:00Z'),
    express: new Date('2022-02-02T15:00Z'),
  },
  29: {
    normal: new Date('2022-01-31T15:00Z'),
    express: new Date('2022-02-07T15:00Z'),
  },
  32: {
    normal: new Date('2022-01-20T15:00Z'),
    express: new Date('2022-02-02T15:00Z'),
  },
  33: {
    normal: new Date('2022-01-20T15:00Z'),
    express: new Date('2022-02-04T15:00Z'),
  },
  34: {
    normal: new Date('2022-01-20T15:00Z'),
    express: new Date('2022-02-04T15:00Z'),
  },
  36: {
    normal: new Date('2022-01-31T15:00Z'),
    express: new Date('2022-02-07T15:00Z'),
  },
}

const getMessage = (t: Function, storeID?: string) => {
  const schedule = valentinesSchedule2022[storeID || '']
  const now = new Date()

  if (!schedule) { return null }

  if (schedule.express && schedule.express < now) {
    // We've passed the point of no return, and/or the final deadline. No message needed.
    return null
  }

  if (schedule.normal && schedule.normal < now) {
    // TODO: refactor this comment and move it one line up.
    // We've passed the normal deadline. Time for express!
    if (!schedule.express) {
      // There is no express deadline available, so... uh... yeah.
      return null
    }
    return t('header:VALENTINES_2022_EXPRESS_DELIVERY_DEADLINE')
  }

  return schedule.normal
    ? t('header:VALENTINES_2022_NORMAL_DELIVERY_DEADLINE')
    : t('header:VALENTINES_2022_EXPRESS_DELIVERY_DEADLINE')
}

const useStoreSchedule = () => {
  const { t } = useTranslation()
  const { store } = useContext(MetadataContext)
  const [message, setMessage] = useState<string>(getMessage(t, store?.id))

  const updateMessage = useCallback(() => setMessage(getMessage(t, store?.id)), [store?.id, t])

  useEffect(() => {
    const schedule = valentinesSchedule2022[store?.id]

    if (!schedule) {
      return noop
    }

    updateMessage()
    const interval = setInterval(updateMessage, 30000)

    return () => clearInterval(interval)
  }, [store?.id, updateMessage])

  return message
}

export const ValentinesBar: FC = () => {
  const deliveryMessage = useStoreSchedule()

  if (!deliveryMessage) {
    return null
  }

  return (
    <StyledBar background="redDark" color="greyLightest">
      {deliveryMessage}
    </StyledBar>
  )
}
